import { BrainCircuit, Radar, Zap } from "lucide-react";

const steps = [
  {
    num: "01",
    icon: BrainCircuit,
    title: "Understand",
    body:
      "EqualLayer interprets what a person actually means — not just the words they type or speak. Context, history, and the page itself all inform intent.",
    stream: ['parse("blue jacket, medium")', 'infer intent → add_to_cart', 'constraints: {size: "M"}'],
  },
  {
    num: "02",
    icon: Radar,
    title: "Find",
    body:
      "It locates the right elements across the page — the product card, the input, the hidden menu — even when they aren't labelled for accessibility.",
    stream: ['scan DOM → 24 candidates', 'rank semantic match', 'selected → product[#id=2]'],
  },
  {
    num: "03",
    icon: Zap,
    title: "Act",
    body:
      "Then it does the thing. Select, fill, submit, navigate — all confirmed with the user, all readable by screen readers, all reversible.",
    stream: ['click(#add-to-cart)', 'verify(cart.total +1)', "announce('added · medium')"],
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how"
      data-testid="how-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col items-start md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-400 shadow-[0_0_10px_rgba(56,189,248,0.9)]" />
              <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-400">
                How it works
              </span>
            </div>
            <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl text-balance">
              How EqualLayer works.
            </h2>
          </div>
          <p className="font-body text-zinc-400 text-base md:text-lg max-w-md leading-relaxed">
            A small, transparent loop runs on every request — understand, find, act — with the human in
            control at every step.
          </p>
        </div>

        <div className="relative mt-16 grid md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={i}
                className="relative bg-[#0A0A0A] p-8 md:p-10 group hover:bg-[#0d0d12] transition-colors"
                data-testid={`step-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-lg border border-indigo-500/30 bg-indigo-500/10 flex items-center justify-center text-indigo-300 shadow-[inset_0_0_20px_rgba(99,102,241,0.15)]">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Step / {step.num}
                  </span>
                </div>

                <h3 className="mt-7 font-display font-medium text-white text-2xl tracking-tight">
                  {step.title}
                </h3>
                <p className="mt-3 font-body text-[15px] leading-relaxed text-zinc-400">
                  {step.body}
                </p>

                <div className="mt-7 rounded-lg border border-white/[0.06] bg-[#050505] p-4 space-y-1.5">
                  {step.stream.map((line, j) => (
                    <div key={j} className="font-mono text-[11.5px] text-zinc-400 flex items-center gap-2">
                      <span className="text-indigo-400/80">›</span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>

                {/* connecting glow */}
                {i < steps.length - 1 && (
                  <div className="pointer-events-none hidden md:block absolute top-1/2 -right-3 h-6 w-6 rounded-full bg-indigo-500/20 blur-md" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
